/* SURMEYI — per-variant buy-button wording.

   THE PROBLEM. Combine decides the Pre-Order label once, at server render, from
   the PRODUCT's `preorder` tag or a block setting, and writes it onto a span as
   data-show-preorder-wording. State is per VARIANT: one product can hold an
   in-stock size and a made-to-order one. Worse, the button sits outside every
   [data-update-block], and component-product-form.js:421-428 copies only
   innerHTML into those blocks - so the attribute is never re-rendered and cannot
   be, whatever we put it on.

   THE FIX. snippets/surmeyi-wording-map renders every variant's DECISION into
   the page as JSON. On each variant change we look up the new id and re-apply.

   WHY THIS RUNS AFTER THE THEME, NOT INSTEAD OF IT. On variant change
   component-product-form.js calls updateBuyButtons() at :446 and only then
   dispatches krown:variant:change at :449, both in one synchronous task - so the
   browser cannot paint between them and correcting the label here is invisible.
   Reaching in earlier would mean patching vendor code, which the next theme
   update would silently revert.

   WE ONLY TOUCH ONE BRANCH. The theme is right about everything else: sold out,
   unavailable, "choose a variant first", bundles. Those stay its business. */
(function () {
  'use strict';

  var KEY_PREORDER = 'preorder';
  var KEY_ARCHIVED = 'archived';

  function locale(name, fallback) {
    try {
      var v = window.KROWN && KROWN.settings && KROWN.settings.locales && KROWN.settings.locales[name];
      return v || fallback;
    } catch (e) { return fallback; }
  }

  /* A CLASS IS NOT A DISABLED CONTROL. The theme marks an unbuyable button with a
     class that brings pointer-events:none, which stops a mouse and nothing else -
     so "Sold Out" sat in the tab order, was announced by a screen reader as an
     ordinary enabled button, and submitted the form on Enter. The Liquid now sets
     the real attribute at render; this keeps it in step afterwards, because the
     vendor's updateBuyButtons toggles only the class. */
  function syncDisabled(component) {
    var btn = component.querySelector('[data-js-product-add-to-cart]');
    if (!btn) return;
    var off = /(^|\s)[\w-]*disabled(\s|$)/.test(btn.className);
    if (off) { btn.setAttribute('disabled', ''); btn.setAttribute('aria-disabled', 'true'); }
    else { btn.removeAttribute('disabled'); btn.removeAttribute('aria-disabled'); }
  }

  function apply(detail) {
    var variant = detail && detail.variant;
    var form = detail && detail.productForm;
    if (!variant || !form) return;

    var comp0 = form.closest('[data-js-product-component]');
    if (comp0) syncDisabled(comp0);

    /* SCOPE TO THE COMPONENT, NOT THE DOCUMENT. A product page can hold the main
       product, related-product cards, a quick-buy drawer and an injected
       quick-view at once - component-product-form.js:322 and component-quick-buy
       .js:474 both fetch ?view=quick-view and splice the result into the CURRENT
       page. A document-wide lookup would find the wrong map and relabel the
       wrong button. */
    var component = form.closest('[data-js-product-component]');
    if (!component) return;

    var holder = component.querySelector('script[type="application/json"][data-surmeyi-wording]');
    if (!holder) return;   // no map here - a card or a context we do not serve

    var map;
    try { map = JSON.parse(holder.textContent); } catch (e) { return; }

    var key = map && map[String(variant.id)];
    if (!key) return;      // unknown variant: leave the theme's label alone

    var span = component.querySelector('[data-js-product-add-to-cart-text]');
    if (!span) return;

    /* Bundles have their own wording and their own button. Not ours. */
    var pv = component.querySelector('product-variants');
    if (pv && pv.hasAttribute('data-show-bundle-wording')) return;

    var btn = component.querySelector('[data-js-product-add-to-cart]');

    /* ARCHIVED IS DECIDED BEFORE AVAILABILITY, and that ordering is the whole point.
       An archived piece can still hold stock - archiving outranks stock by design -
       so variant.available is TRUE, the vendor's updateBuyButtons writes "Add to
       cart" and strips the disabled class, and syncDisabled above then faithfully
       removes the attribute to match. Everything downstream agrees, and all of it is
       wrong: the page offers a working button on a piece that has been withdrawn.

       This function used to return at `if (!variant.available)` before it ever read
       the map, so the Liquid's Archived label survived exactly until the shopper
       changed variant and came back. */
    if (key === KEY_ARCHIVED) {
      span.removeAttribute('data-show-preorder-wording');
      if (span.textContent.trim() !== 'Archived') span.textContent = 'Archived';
      if (btn) {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('disabled', 'button--outline');
        btn.classList.remove('button--solid');
      }
      return;
    }

    /* Coming BACK from an archived variant, the stroke has to go with it. */
    if (btn && btn.classList.contains('button--outline')) {
      btn.classList.remove('button--outline');
      btn.classList.add('button--solid');
    }

    /* The vendor owns every other branch. A variant that cannot be bought must keep
       saying so - overwriting that with Pre-order would offer a button that does
       nothing. Archived is handled above precisely because it is NOT this case. */
    if (!variant.available) return;

    var wantPreorder = key === KEY_PREORDER;

    /* Keep the attribute honest as well as the text. Anything that calls
       updateBuyButtons() again - a quantity change, a re-render - reads the
       attribute, and leaving it stale would flip the label back. */
    if (wantPreorder) span.setAttribute('data-show-preorder-wording', '');
    else span.removeAttribute('data-show-preorder-wording');

    var text = wantPreorder
      /* THE FALLBACKS MOVED TO SENTENCE CASE ON 2026-09-14 with the locale file, and
         they have to move together: this string is what a shopper reads if the locale
         bundle is ever missing the key, so a stale fallback here is a button that
         silently disagrees with every other button on the page. */
      ? locale('products_preorder_button', 'Pre-order')
      : locale('products_add_to_cart_button', 'Add to cart');

    if (span.textContent.trim() !== text) span.textContent = text;
  }

  document.addEventListener('krown:variant:change', function (e) {
    try { apply(e.detail); } catch (err) { /* never break the buy button */ }
  });
})();
